import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '@shared/infrastructure/ui/layout/dashboardLayout';
import {
  TablePlain,
  TableCell,
  TableRow
} from '@shared/infrastructure/ui/components/table/tablePlain/tablePlain';
import { Topic } from '@shared/domain/topic/topic.aggregate';
import { topicRepository } from '@shared/domain/topic/topic.repository';
import Switch from '@mui/material/Switch';
import { useAppSelector } from '@shared/infrastructure/state/store';
import {
  countryCodeSelector,
  languageCodeSelector
} from '@shared/infrastructure/state/global.slice';
import { PageHeader } from '@shared/infrastructure/ui/components/header/pageHeader';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  IconButton,
  Tooltip
} from '@mui/material';
import VrpanoIcon from '@mui/icons-material/Vrpano';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/CreateOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { HorizontalSpaceBetweenLayout } from '@shared/infrastructure/ui/layout/horizontalSpaceBetweenLayout';
import { TopicModal } from './topicModal';
import { historyRepository } from '@shared/domain/router/router.repository';
import { routeRepository } from '@shared/domain/router/route/route.repository';
import { ROUTES } from '@shared/infrastructure/router/paths';
import { subtopicRepository } from '@shared/domain/subtopic/subtopic.repository';
import { Subtopic } from '@shared/domain/subtopic/subtopic.aggregate';

const Row = ({
  topic,
  countryCode,
  onDelete
}: {
  topic: Topic;
  countryCode: string;
  onDelete: () => void;
}) => {
  const [isEnabled, setIsEnabled] = useState<boolean>(topic.enabled);
  useEffect(() => {
    setIsEnabled(topic.enabled);
  }, [topic.enabled, countryCode]);

  const [panoramicImageUrl, setPanoramicImageUrl] = useState<
    string | undefined
  >(topic.panoramicImageUrl);
  useEffect(() => {
    setPanoramicImageUrl(topic.panoramicImageUrl);
  }, [topic.panoramicImageUrl, countryCode]);

  return (
    <TableRow
      key={topic.id}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      aria-label='topics table row'
    >
      <TableCell component='th' scope='row'>
        {topic.nameLanguage}
      </TableCell>
      <TableCell>{topic.therapyNameLanguage}</TableCell>
      <TableCell>
        <Switch
          checked={isEnabled}
          onClick={() => {
            topicRepository
              .toggleEnableTopic(topic.id, countryCode)
              .then(() => setIsEnabled((p) => !p));
          }}
        />
      </TableCell>
      <TableCell component='th' scope='row'>
        <Tooltip title='Edit topic' placement='left-start'>
          <IconButton
            size='large'
            aria-label='Edit topic'
            onClick={() =>
              historyRepository.push(
                routeRepository.buildRoute(ROUTES.TOPICS_EDIT.path, {
                  therapyId: String(topic.therapyId),
                  topicId: String(topic.id)
                })
              )
            }
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        {/* <Tooltip title='Delete topic' placement='left-start'>
          <IconButton size='large' aria-label='Delete topic' onClick={onDelete}>
            <DeleteIcon />
          </IconButton>
        </Tooltip> */}
        <Tooltip title='View background image' placement='left-start'>
          <IconButton
            size='large'
            aria-label='View background image'
            target='_blank'
            rel='noreferrer'
            href={panoramicImageUrl ?? ''}
          >
            <VrpanoIcon></VrpanoIcon>
          </IconButton>
        </Tooltip>
        <Tooltip title='Upload background image' placement='left-start'>
          <IconButton
            size='large'
            aria-label='Upload background image'
            component='label'
          >
            <DriveFolderUploadIcon></DriveFolderUploadIcon>
            <input
              type='file'
              hidden
              onChange={(e) => {
                if (e.target && e.target.files && e.target.files[0]) {
                  topicRepository
                    .uploadTopicImgBackground(
                      topic.id,
                      countryCode,
                      e.target.files[0]
                    )
                    .then((location) => setPanoramicImageUrl(location));
                }
              }}
            />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const DeleteDialog = ({
  topic,
  subtopicCount,
  onCancel,
  onAccept
}: {
  topic?: Topic;
  subtopicCount: number;
  onCancel?: () => void;
  onAccept?: (subtopic: Topic) => void;
}) => {
  return (
    <Dialog
      open={!!topic}
      onClose={onCancel}
      aria-labelledby='delete-dialog-title'
      aria-describedby='delete-dialog-description'
    >
      <DialogTitle id='delete-dialog-title'>Are you sure?</DialogTitle>
      <DialogContent>
        <DialogContentText id='delete-dialog-description'>
          The topic will be permanently lost.
          {subtopicCount > 0 ? (
            <div>
              <br />
              {subtopicCount} subtopic(s) are attached to this topic, which will
              be deleted as well.
            </div>
          ) : (
            <div></div>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onCancel} autoFocus>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='error'
          onClick={() => onAccept?.(topic!)}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const TopicView = () => {
  const countryCode = useAppSelector(countryCodeSelector);
  const languageCode = useAppSelector(languageCodeSelector);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [subtopics, setSubtopics] = useState<Subtopic[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteTopic, setDeleteTopic] = useState<Topic>();

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);

  useEffect(() => {
    topicRepository
      .getTopics(countryCode, languageCode)
      .then((topics) => setTopics(topics || []));
  }, [countryCode, languageCode]);

  useEffect(() => {
    subtopicRepository
      .getAllSubtopics(countryCode, languageCode)
      .then((subtopics) => setSubtopics(subtopics));
  }, [countryCode, languageCode]);

  return (
    <>
      {isModalOpen && (
        <TopicModal isOpen={isModalOpen} onClose={handleClose}></TopicModal>
      )}
      <Fab
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        color='primary'
        aria-label='Create'
        onClick={() =>
          historyRepository.push(
            routeRepository.buildRoute(ROUTES.TOPICS_CREATE.path, {})
          )
        }
      >
        <AddIcon />
      </Fab>
      <DashboardLayout>
        <HorizontalSpaceBetweenLayout>
          <>
            <PageHeader>
              <>Topics View</>
            </PageHeader>
            <IconButton
              size='large'
              aria-label='topics info button'
              onClick={handleOpen}
            >
              <InfoOutlinedIcon></InfoOutlinedIcon>
            </IconButton>
          </>
        </HorizontalSpaceBetweenLayout>
        <TablePlain<Topic[]>
          data={topics}
          headers={['Name', 'Therapy', 'Enabled', '']}
        >
          {(data: Topic[]) =>
            data.map((topic: Topic) => (
              <Row
                key={topic.id}
                topic={topic}
                countryCode={countryCode}
                onDelete={() => setDeleteTopic(topic)}
              ></Row>
            ))
          }
        </TablePlain>
        <DeleteDialog
          topic={deleteTopic}
          subtopicCount={
            subtopics.filter((x) => x.topicId === deleteTopic?.id).length
          }
          onCancel={() => setDeleteTopic(undefined)}
          onAccept={(topic) => {
            topicRepository.deleteTopic(countryCode, topic.id).then(() => {
              setTopics((topics) => topics.filter((s) => s.id !== topic.id));
              setDeleteTopic(undefined);
            });
          }}
        />
      </DashboardLayout>
    </>
  );
};
